import React, { useEffect, useRef, useState } from 'react';
import Lang from '../../common/model/global/Lang';
import HwzContentCard from '../widget/HwzContentCard';
import HwzTitle from '../widget/HwzTitle';
import Preference from '../../common/model/preference/Preference';
import { Button, Form, Select, Switch, InputNumber, Alert } from 'antd';
import { SaveOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  ScanCronTypeList,
} from '../../common/model/preference/model/ScanCronType';
import {
  ScanScopeType,
  ScanScopeTypeList,
} from '../../common/model/preference/model/ScanScopeType';
import Space from '../../common/model/space/Space';
import Pager from '../../common/model/base/Pager';
import SortDirection from '../../common/model/base/SortDirection';
import MessageBoxUtil from '../../common/util/MessageBoxUtil';
import Sun from '../../common/model/global/Sun';
import { debounce } from '../../common/util/OptimizeUtil';

const ScanEdit = () => {
  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };
  const pager = useRef(new Pager<Space>(null, Space, Pager.MAX_PAGE_SIZE));
  const preference = useRef(new Preference());
  const [loading, setLoading] = useState(true);
  const [spaceList, setSpaceList] = useState<Space[]>([]);
  const [form] = Form.useForm();

  const handleFinish = async () => {
    const values = await form.validateFields();
    const submitData = {
      ...values,
      // 当自动清理关闭时，传递 0（与后端 int 类型匹配）
      autoCleanDays: values.autoCleanEnabled ? values.autoCleanDays : 0,
    };
    preference.current.scanConfig.assign(submitData);
    preference.current.httpSaveScan(function () {
      MessageBoxUtil.success(Lang.t('operationSuccess'));
      Sun.updateFrame();
      Sun.navigateTo('/preference/index');
    });
  };

  const handleRefreshSpace = debounce((keyword?: string) => {
    pager.current.setFilterValue('orderCreateTime', SortDirection.DESC);
    pager.current.setFilterValue('name', keyword);
    pager.current.httpList(() => setSpaceList(pager.current.data));
  }, 200);

  useEffect(() => {
    preference.current.detailLoading = true;
    preference.current.httpFetch(() => {
      // 设置默认值（scanConfig 内部已处理默认值）
      setLoading(false);
      
      // 重置表单，字段与 ScanConfig 的 getForm 返回值一致
      form.setFieldsValue({
        enable: preference.current.scanConfig.enable,
        cron: preference.current.scanConfig.cron || '0 0 * * *',
        scope: preference.current.scanConfig.scope || ScanScopeType.ALL,
        spaceNames: preference.current.scanConfig.spaceNames || [],
        autoCleanEnabled: preference.current.scanConfig.autoCleanEnabled,
        autoCleanDays: preference.current.scanConfig.autoCleanDays || 30, // 默认30天
      });
    });
    handleRefreshSpace();
  }, []);

  return (
    <div className="preference-scan-edit">
      <HwzTitle name={Lang.t('preference.editScan')} />
      <HwzContentCard loading={loading}>
        <Form
          {...layout}
          form={form}
          onFinish={handleFinish}
        >
          {/* 开启/关闭自动扫描开关 */}
          <Form.Item
            label={Lang.t('preference.enableScan')}
            name="enable"
            valuePropName="checked"
            extra={Lang.t('preference.enableScanDesc', '开启后系统将按照设定的周期自动执行扫描')}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={Lang.t('preference.scanCron')}
            name="cron"
            required
            rules={[
              {
                required: true,
                message: Lang.t('preference.scanCronValidate'),
              },
            ]}
          >
            <Select>
              {ScanCronTypeList.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={Lang.t('preference.scanScope')}
            name="scope"
            required
            rules={[
              {
                required: true,
                message: Lang.t('preference.scanScopeValidate'),
              },
            ]}
          >
            <Select>
              {ScanScopeTypeList.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item dependencies={['scope']} noStyle>
            {({ getFieldValue }) => {
              return (
                getFieldValue('scope') === ScanScopeType.CUSTOM && (
                  <Form.Item
                    label={Lang.t('preference.scanSpaces')}
                    name="spaceNames"
                    rules={[
                      {
                        required: true,
                        message: Lang.t('preference.chooseSpacesValidate'),
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder={Lang.t('preference.chooseSpaces')}
                      className="wp100"
                      filterOption={false}
                      onSearch={handleRefreshSpace}
                    >
                      {spaceList.map((space: Space) => (
                        <Select.Option key={space.name} value={space.name}>
                          {space.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )
              );
            }}
          </Form.Item>

          {/* 自动清理配置 */}
          <Form.Item
            label={Lang.t('preference.autoClean')}
            name="autoCleanEnabled"
            valuePropName="checked"
            extra={
              <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                {Lang.t('preference.autoCleanDesc', '开启后系统将自动清理指定天数前的文件(非摆渡环境不建议开启)')}
              </div>
            }
          >
            <Switch />
          </Form.Item>

          <Form.Item dependencies={['autoCleanEnabled']} noStyle>
            {({ getFieldValue }) => {
              const autoCleanEnabled = getFieldValue('autoCleanEnabled');
              return autoCleanEnabled && (
                <>
                  <Alert
                    message={
                      <div style={{ color: '#ff4d4f' }}>
                        <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                        {Lang.t('preference.autoCleanWarning', '开启本功能会被清理的旧文件彻底丢失，慎重操作！')}
                      </div>
                    }
                    type="error"
                    showIcon={false}
                    style={{ 
                      marginBottom: 16,
                      backgroundColor: '#fff2f0',
                      border: '1px solid #ffccc7',
                      borderRadius: 4,
                      padding: '12px 16px'
                    }}
                  />
                  
                  <Form.Item
                    label={Lang.t('preference.cleanBeforeDays')}
                    name="autoCleanDays"
                    rules={[
                      {
                        required: true,
                        message: Lang.t('preference.cleanBeforeDaysValidate', '请输入清理天数'),
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 3650,
                        message: Lang.t('preference.cleanBeforeDaysRange', '请输入1-3650之间的数字'),
                      },
                    ]}
                    extra={
                      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                        {Lang.t('preference.cleanBeforeDaysDesc', '将清理此天数之前的文件，例如设置为30天，则会清理30天前的文件')}
                      </div>
                    }
                  >
                    <InputNumber
                      min={1}
                      max={3650}
                      addonAfter={Lang.t('preference.day', '天')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  
                  <Alert
                    message={Lang.t('preference.autoCleanWarningDesc', '重要提示')}
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: 16 }}
                    description={
                      <div>
                        <p>{Lang.t('preference.autoCleanWarningDescText', '开启自动清理功能后，系统将按照设定的时间周期自动清理符合条件的文件。被清理的文件将彻底丢失，无法恢复。')}</p>
                        <p style={{ marginTop: 8, fontWeight: 'bold' }}>
                          {Lang.t('preference.autoCleanCaution', '请确保您了解此功能的影响，并谨慎设置清理天数。')}
                        </p>
                      </div>
                    }
                  />
                </>
              );
            }}
          </Form.Item>

          <div className="text-right">
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              {Lang.t('save', '保存')}
            </Button>
          </div>
        </Form>
      </HwzContentCard>
    </div>
  );
};

export default ScanEdit;